import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { number, relative } from '../lib/format';
import { Card, Empty, ErrorNote, Loading, PageHead, Pill, Stat, useResource } from '../components/ui';
import RegisterCard from '../components/RegisterCard';

interface Dashboard {
  totals: { principals: number; live_consents: number; withdrawals_30d: number; live_notice_versions: number };
  coverage: Array<{
    purpose_id: string; purpose_name: string; status: string; basis: string;
    granted: number; declined: number; withdrawn: number; expired: number;
  }>;
  trend: Array<{ day: string; granted: number; declined: number; withdrawn: number }>;
  propagation: { delivered: number; pending: number; failed: number; health: 'ok' | 'retrying' | 'failing' };
  needsAttention: Array<{ kind: string; entity_id: string; label: string; detail: string }>;
  lastVerification: { status: string; events_checked: number; finished_at: string; break_reason: string | null } | null;
  touchpoints: Array<{
    id: string; name: string; channel: string; status: string; purposes: number;
    notice_version: number | null; form_live: boolean; people: number;
    granted: number; declined: number; informed: number; last_capture: string | null;
  }>;
  requests: { open: number; overdue: number; unacknowledged: number };
}

const CHANNEL: Record<string, string> = {
  web: 'Website', app: 'Mobile app', api: 'System integration', offline: 'Paper or in person',
  callcentre: 'Call centre', cookie: 'Cookie banner',
  paper: 'Paper form', kiosk: 'Kiosk', ivr: 'Phone (IVR)', email: 'Email', sms: 'SMS', whatsapp: 'WhatsApp',
};

const FIX_ROUTE: Record<string, string> = {
  purpose_without_notice: '/notices',
  collection_point_without_published_notice: '/notices',
  collection_point_retired_purpose: '/collection-points',
};

interface Funnel {
  stages: { requested: number; viewed: number; decided: number; granted: number };
  direct: { decided: number; granted: number };
}

interface DecisionTime {
  decided: number;
  medianSeconds: number | null;
  p90Seconds: number | null;
  buckets: Array<{ key: string; label: string; count: number }>;
}

/** Seconds as the largest whole unit that reads naturally: 45 min, 3 h, 2.5 d. */
function duration(seconds: number | null): string {
  if (seconds === null) return '—';
  if (seconds < 3_600) return `${Math.max(1, Math.round(seconds / 60))} min`;
  if (seconds < 86_400) return `${Math.round((seconds / 3_600) * 10) / 10} h`;
  return `${Math.round((seconds / 86_400) * 10) / 10} d`;
}

export default function DashboardPage() {
  const [days, setDays] = useState(90);
  const { data, error, loading, reload } = useResource<Dashboard>(
    () => api.get<Dashboard>(`/v1/dashboard?days=${days}`),
    [days],
  );
  // Separate reads: the dashboard renders without them if either fails.
  const funnel = useResource<Funnel>(() => api.get<Funnel>(`/v1/analytics/funnel?days=${days}`), [days]);
  const timing = useResource<DecisionTime>(() => api.get<DecisionTime>(`/v1/analytics/decision-time?days=${days}`), [days]);

  return (
    <>
      <PageHead
        title="Dashboard"
      >
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} style={{ width: 140 }}>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={180}>Last 180 days</option>
          <option value={365}>Last year</option>
        </select>
        <button onClick={reload}>Refresh</button>
      </PageHead>

      <ErrorNote error={error} />
      {loading && !data && <Loading what="Loading dashboard" />}

      {data && (
        <>
          {data.needsAttention.length > 0 && (
            <div className="card" style={{ borderColor: 'var(--warn-border)', background: 'var(--warn-bg)', marginBottom: 14 }}>
              <h3 style={{ color: 'var(--warn)' }}>Needs attention</h3>
              {data.needsAttention.map((n) => (
                <div key={`${n.kind}-${n.entity_id}`} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '7px 0' }}>
                  <div style={{ flex: 1 }}>
                    <strong>{n.label}</strong>
                    <div className="small muted">{n.detail}</div>
                  </div>
                  <Link className="btn small" to={FIX_ROUTE[n.kind] ?? '/purposes'}>Fix</Link>
                </div>
              ))}
            </div>
          )}

          <div className="grid cols-4">
            <Stat label="Data principals" value={number(data.totals.principals)} />
            <Stat label="Active consents" value={number(data.totals.live_consents)} sub="granted, not expired" />
            <Stat
              label="Withdrawals"
              value={number(data.totals.withdrawals_30d)}
              tone={data.propagation.failed > 0 ? 'bad' : undefined}
              sub={data.propagation.failed > 0
                ? `last 30 days · ${number(data.propagation.failed)} processor notifications failed`
                : data.propagation.pending > 0
                  ? `last 30 days · ${number(data.propagation.pending)} processor notifications pending`
                  : 'last 30 days · processors notified'}
            />
            <Stat
              label="Rights requests"
              value={number(data.requests.open)}
              tone={data.requests.overdue > 0 ? 'bad' : data.requests.unacknowledged > 0 ? 'warn' : undefined}
              sub={data.requests.overdue > 0
                ? `open · ${number(data.requests.overdue)} past due`
                : data.requests.unacknowledged > 0
                  ? `open · ${number(data.requests.unacknowledged)} not yet acknowledged`
                  : 'open · none past due'}
            />
          </div>

          <div style={{ marginTop: 14 }} />
          <Card title="Coverage by touch point">
            {data.touchpoints.length === 0 ? (
              <Empty>No collection points.</Empty>
            ) : (
              <div className="table-scroll">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Touch point</th>
                      <th>Channel</th>
                      <th>Notice</th>
                      <th>Form</th>
                      <th className="num">People asked</th>
                      <th>Outcomes</th>
                      <th>Last capture</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.touchpoints.map((tp) => {
                      const total = tp.granted + tp.declined;
                      const pct = (n: number) => (total ? `${(n / total) * 100}%` : '0%');
                      return (
                        <tr key={tp.id}>
                          <td>
                            <Link to="/collection-points">{tp.name}</Link>
                            {tp.status !== 'active' && <> <Pill>{tp.status}</Pill></>}
                          </td>
                          <td>{CHANNEL[tp.channel] ?? tp.channel}</td>
                          <td>{tp.notice_version ? <Pill tone="ok" tag>v{tp.notice_version} in force</Pill> : <Pill tone="bad" tag>None published</Pill>}</td>
                          <td>{tp.channel === 'cookie' ? <span className="muted">Banner</span> : tp.form_live ? <Pill tone="ok">Published</Pill> : <span className="muted">None</span>}</td>
                          <td className="num">{number(tp.people)}</td>
                          <td style={{ minWidth: 180 }}>
                            <div className="tp-track" title={`${tp.granted} granted · ${tp.declined} declined${tp.informed ? ` · ${tp.informed} stated under S.7` : ''}`}>
                              <div className="seg granted" style={{ width: pct(tp.granted) }} />
                              <div className="seg declined" style={{ width: pct(tp.declined) }} />
                            </div>
                            <div className="small muted">{number(tp.granted)} granted · {number(tp.declined)} declined</div>
                          </td>
                          <td className="small">{tp.last_capture ? relative(tp.last_capture) : <span className="muted">Never</span>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <div className="grid cols-2" style={{ marginTop: 14 }}>
            <Card title="Coverage by purpose">
              {data.coverage.length === 0 ? (
                <Empty>No purposes yet.</Empty>
              ) : (
                <>
                  {data.coverage.map((c) => {
                    const total = c.granted + c.declined + c.withdrawn + c.expired;
                    const pct = (n: number) => (total ? `${(n / total) * 100}%` : '0%');
                    if (c.basis === 'legitimate_use') {
                      return (
                        <div className="bar-row" key={c.purpose_id}>
                          <div className="label" title={c.purpose_name}>{c.purpose_name}</div>
                          <div className="small muted" style={{ flex: 1 }}>Stated under S.7, no consent taken</div>
                          <div className="total" />
                        </div>
                      );
                    }
                    return (
                      <div className="bar-row" key={c.purpose_id}>
                        <div className="label" title={c.purpose_name}>
                          {c.purpose_name}
                          {c.status !== 'active' && <> <Pill>{c.status}</Pill></>}
                        </div>
                        <div className="track" title={`${c.granted} granted · ${c.declined} declined · ${c.withdrawn} withdrawn`}>
                          <div className="seg granted" style={{ width: pct(c.granted) }} />
                          <div className="seg declined" style={{ width: pct(c.declined) }} />
                          <div className="seg withdrawn" style={{ width: pct(c.withdrawn + c.expired) }} />
                        </div>
                        <div className="total">{number(total)}</div>
                      </div>
                    );
                  })}
                  <Legend />
                </>
              )}
            </Card>

            <Card title={`Collection and withdrawal · ${days} days`}>
              <Trend rows={data.trend} />
              <Legend />
            </Card>
          </div>

          <div className="grid cols-2" style={{ marginTop: 14 }}>
            <Card title={`Funnel · ${days} days`}>
              <ErrorNote error={funnel.error} />
              {funnel.data && (funnel.data.stages.requested === 0 ? (
                <Empty>No consent requests in this period.</Empty>
              ) : (
                ([['Asked', funnel.data.stages.requested], ['Viewed', funnel.data.stages.viewed], ['Decided', funnel.data.stages.decided], ['Granted', funnel.data.stages.granted]] as Array<[string, number]>).map(([label, n]) => (
                  <div className="bar-row" key={label}>
                    <div className="label">{label}</div>
                    <div className="track" title={`${n} of ${funnel.data!.stages.requested}`}>
                      <div className="seg granted" style={{ width: `${(n / funnel.data!.stages.requested) * 100}%` }} />
                    </div>
                    <div className="total">{number(n)}</div>
                  </div>
                ))
              ))}
              {funnel.data && (
                <div className="small muted">Decided without a request: {number(funnel.data.direct.decided)} · granted {number(funnel.data.direct.granted)}</div>
              )}
            </Card>
            <Card title={`Decision time · ${days} days`}>
              <ErrorNote error={timing.error} />
              {timing.data && (timing.data.decided === 0 ? (
                <Empty>No decisions after a request in this period.</Empty>
              ) : (
                <>
                  <div className="small" style={{ marginBottom: 10 }}>
                    Median {duration(timing.data.medianSeconds)} · 90th percentile {duration(timing.data.p90Seconds)} · {number(timing.data.decided)} decided
                  </div>
                  {timing.data.buckets.map((b) => (
                    <div className="bar-row" key={b.key}>
                      <div className="label">{b.label}</div>
                      <div className="track">
                        <div className="seg granted" style={{ width: `${(b.count / timing.data!.decided) * 100}%` }} />
                      </div>
                      <div className="total">{number(b.count)}</div>
                    </div>
                  ))}
                </>
              ))}
            </Card>
          </div>

          <div className="grid cols-2" style={{ marginTop: 14 }}>
            <Card title="Processors notified · 7 days">
              <div className="stat">
                <div className={`value ${data.propagation.failed ? 'bad' : data.propagation.pending ? 'warn' : 'ok'}`}>
                  {number(data.propagation.delivered)}
                  <span className="muted" style={{ fontSize: '0.5em', fontWeight: 400 }}> of {number(data.propagation.delivered + data.propagation.pending + data.propagation.failed)}</span>
                </div>
                <div className="sub">
                  withdrawals relayed to processors
                  {data.propagation.pending > 0 && ` · ${number(data.propagation.pending)} pending`}
                  {data.propagation.failed > 0 && ` · ${number(data.propagation.failed)} failed`}
                </div>
              </div>
              <div style={{ marginTop: 12 }}>
                <Link className="btn small" to="/propagation">Open notifications</Link>
              </div>
            </Card>
            <Card title="Notices in force">
              <div className="stat">
                <div className="value">{number(data.totals.live_notice_versions)}</div>
                <div className="sub">published versions in force</div>
              </div>
              <div style={{ marginTop: 12 }}>
                <Link className="btn small" to="/notices">Manage notices</Link>
              </div>
            </Card>
          </div>
        </>
      )}
      <div style={{ marginTop: 14 }} />
      <RegisterCard />
    </>
  );
}

function Trend({ rows }: { rows: Dashboard['trend'] }) {
  if (!rows.length) return <Empty>No events in this period.</Empty>;
  const max = Math.max(...rows.map((r) => r.granted + r.declined + r.withdrawn), 1);

  return (
    <div className="spark" role="img" aria-label="Daily consent events">
      {rows.map((r) => {
        const h = (n: number) => `${(n / max) * 100}%`;
        return (
          <div
            className="bar"
            key={r.day}
            title={`${r.day.slice(0, 10)} · ${r.granted} granted, ${r.declined} declined, ${r.withdrawn} withdrawn`}
          >
            <div className="seg withdrawn" style={{ height: h(r.withdrawn) }} />
            <div className="seg declined" style={{ height: h(r.declined) }} />
            <div className="seg granted" style={{ height: h(r.granted) }} />
          </div>
        );
      })}
    </div>
  );
}

function Legend() {
  return (
    <div className="legend">
      <span className="key"><span className="swatch" style={{ background: 'var(--brand)' }} /> granted</span>
      <span className="key"><span className="swatch" style={{ background: 'var(--brand-light)' }} /> declined</span>
      <span className="key"><span className="swatch" style={{ background: 'var(--accent)' }} /> withdrawn or expired</span>
    </div>
  );
}
